import { useState } from "react";
import { useProviders, type Provider } from "../../api/admin";
import { formatMoney, formatPercent, toCents } from "../../api/money";
import { StatusChip } from "../../components/ui";
import { AdminDrawer, ListSearch, matches } from "./AdminDrawer";
import AdminLayout from "./AdminLayout";
import { CreateProvider, EditProvider } from "./ProviderForms";

function kindText(p: Provider): string {
  if (p.kinds.length === 0) {
    return "—";
  }
  const words = p.kinds.map((kind) => (kind === "crew" ? "Field operators" : "Supplier"));
  return words.join(", ");
}

/** A rate or a discount of nought is not a rate: shown as a dash, so a crew
 * row does not read as a supplier giving 0%. */
function orDash(value: string | null, format: (v: string) => string): string {
  return value === null || toCents(value) === 0 ? "—" : format(value);
}

/** Service providers: the crews and the suppliers, their letterhead details
 * and rates, the shared crew login, and the artwork a quotation prints. */
export default function ProvidersPage() {
  const list = useProviders();
  const [editing, setEditing] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const rows = (list.data?.rows ?? []).filter((p) => matches(query, [p.name, p.acc_no, p.email, p.contact_person]));
  const chosen = list.data?.rows.find((p) => p.id === editing);

  return (
    <AdminLayout
      title="Providers"
      sub="Field operators and suppliers, with their rates"
      actions={
        <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
          + Add provider
        </button>
      }
    >
      <ListSearch value={query} onChange={setQuery} placeholder="Search providers" />
      {list.isError ? <p className="notice bad">The providers could not be loaded: {list.error.message}</p> : null}
      {list.isPending ? <div className="table-card empty-state">Loading providers…</div> : null}
      {list.data && list.data.rows.length === 0 ? (
        <div className="table-card empty-state">
          <strong>No providers yet.</strong> Add the first field operator or supplier.
        </div>
      ) : null}
      {list.data && list.data.rows.length > 0 ? (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th>Acc no.</th>
                <th>Discount</th>
                <th>Labour / h</th>
                <th>Per km</th>
                <th className="num">Logins</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className={p.id === editing ? "selected" : undefined}>
                  <td>
                    {p.name}
                    {p.login ? <span className="sub">{p.login.email}</span> : null}
                  </td>
                  <td>{kindText(p)}</td>
                  <td className="mono">{p.acc_no}</td>
                  <td>{orDash(p.discount_pct, formatPercent)}</td>
                  <td>{orDash(p.labour_rate, formatMoney)}</td>
                  <td>{orDash(p.km_rate, formatMoney)}</td>
                  <td className="num">{p.logins}</td>
                  <td>
                    <StatusChip tone={p.active ? "ok" : "quiet"}>{p.active ? "Active" : "Inactive"}</StatusChip>
                  </td>
                  <td className="num">
                    <button type="button" className="row-button" onClick={() => setEditing(p.id)}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="empty-state">
                    No provider matches “{query}”.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
      {adding ? (
        <AdminDrawer title="Add a service provider" onClose={() => setAdding(false)} wide>
          <CreateProvider onDone={() => setAdding(false)} />
        </AdminDrawer>
      ) : null}
      {chosen ? (
        <AdminDrawer title={chosen.name} sub={chosen.acc_no} onClose={() => setEditing(null)} wide>
          <EditProvider key={chosen.id} provider={chosen} onDone={() => setEditing(null)} />
        </AdminDrawer>
      ) : null}
    </AdminLayout>
  );
}
